/**
 * FrontDeskDisplayPage — Customer-Facing 2nd Screen Shell
 *
 * Orchestrates: idle → order_review → tip → payment_select → processing → complete → receipt → idle
 * Connected via SSE to /api/display/stream/:terminalId
 * Loads branding config from /api/display/config/:restaurantId (the signed-in
 * restaurant's own Display Settings)
 */
import React, { useCallback, useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import useCustomerDisplaySession, { PHASE } from '../hooks/useCustomerDisplaySession';
import CustomerIdleScreen from '../components/CustomerIdleScreen';
import CustomerCheckoutWakeScreen from '../components/CustomerCheckoutWakeScreen';
import CustomerTipSelection from '../components/CustomerTipSelection';
import CustomerPaymentMethodSelection from '../components/CustomerPaymentMethodSelection';
import CustomerProcessingScreen from '../components/CustomerProcessingScreen';
import CustomerPaymentCompleteScreen from '../components/CustomerPaymentCompleteScreen';
import CustomerReceiptOptionScreen from '../components/CustomerReceiptOptionScreen';
import CustomerDisplayStatusBadge from '../components/CustomerDisplayStatusBadge';
import { useAccount } from '@/lib/pwaAccount';
import './CustomerDisplay.css';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function FrontDeskDisplayPage() {
  const { terminalId = 'default' } = useParams();
  const [searchParams] = useSearchParams();
  const account = useAccount();
  const restaurantName = searchParams.get('name') || account?.restaurant?.name || 'Chain29';
  // Display Settings are saved under the restaurant's own id (the server
  // refuses any other key), so the screen reads its signed-in restaurant's.
  const configKey = account?.restaurant?.id ? String(account.restaurant.id) : null;

  // Branding config from backend
  const [brandConfig, setBrandConfig] = useState({});

  useEffect(() => {
    if (!configKey) return;
    fetch(`${API}/api/display/config/${configKey}`)
      .then(r => r.json())
      .then(d => { if (d.success && d.config) setBrandConfig(d.config); })
      .catch(() => {});
  }, [configKey]);

  const {
    phase, setPhase,
    connected,
    checkout,
    tipAmount, setTipAmount,
    paymentStatus,
    resultType, resultMessage, receiptData,
    notifyPOS,
    resetToIdle,
    scheduleIdle,
  } = useCustomerDisplaySession(terminalId);

  const subtotal = checkout?.subtotal || 0;
  const tax = checkout?.tax || 0;
  const total = subtotal + tax + tipAmount;
  const paymentMethods = checkout?.payment_methods_available || ['cash', 'card', 'zelle', 'venmo', 'loyalty', 'gift', 'crypto', 'qr'];

  // Delayed reconnecting overlay
  const [showReconnecting, setShowReconnecting] = useState(false);
  useEffect(() => {
    if (!connected && phase !== PHASE.IDLE) {
      const t = setTimeout(() => setShowReconnecting(true), 3000);
      return () => clearTimeout(t);
    }
    setShowReconnecting(false);
  }, [connected, phase]);

  // Handlers
  const handleTipConfirm = useCallback((tip) => {
    setTipAmount(tip);
    notifyPOS('TIP_SELECTED', { tip_amount: tip, total: subtotal + tax + tip });
    setPhase(PHASE.PAYMENT_SELECT);
  }, [subtotal, tax, setTipAmount, notifyPOS, setPhase]);

  const handlePaymentSelect = useCallback((method) => {
    notifyPOS('PAYMENT_METHOD_SELECTED', { payment_method: method });
    setPhase(PHASE.PROCESSING);
  }, [notifyPOS, setPhase]);

  const handleReceiptSelect = useCallback((type, phone, email) => {
    notifyPOS('RECEIPT_REQUESTED', {
      receipt_type: type,
      receipt_phone: phone,
      receipt_email: email,
    });
    scheduleIdle(6000);
    setPhase(PHASE.IDLE);
  }, [notifyPOS, scheduleIdle, setPhase]);

  const handleCompleteContinue = useCallback(() => {
    setPhase(PHASE.RECEIPT);
  }, [setPhase]);

  const displayName = brandConfig.restaurant_name || restaurantName;

  return (
    <div className="cfd-shell" data-testid="customer-display">
      {phase === PHASE.IDLE && (
        <CustomerIdleScreen
          restaurantName={displayName}
          logoUrl={brandConfig.logo_url || ''}
          idleImageUrl={brandConfig.idle_image_url || ''}
          tagline={brandConfig.tagline || ''}
          subtext={brandConfig.subtext || 'Please review and pay here when checkout begins'}
          accentColor={brandConfig.accent_color || '#d4a053'}
          welcomeMessages={brandConfig.welcome_messages}
          promoTitle={brandConfig.promo_title || ''}
          promoText={brandConfig.promo_text || ''}
        />
      )}

      {phase === PHASE.ORDER_REVIEW && checkout && (
        <CustomerCheckoutWakeScreen
          checkout={checkout}
          onContinue={() => setPhase(PHASE.TIP_SELECT)}
        />
      )}

      {phase === PHASE.TIP_SELECT && (
        <CustomerTipSelection
          subtotal={subtotal}
          tax={tax}
          tipAmount={tipAmount}
          onConfirm={handleTipConfirm}
        />
      )}

      {phase === PHASE.PAYMENT_SELECT && (
        <CustomerPaymentMethodSelection
          methods={paymentMethods}
          total={total}
          onSelect={handlePaymentSelect}
          onBack={() => setPhase(PHASE.TIP_SELECT)}
        />
      )}

      {phase === PHASE.PROCESSING && (
        <CustomerProcessingScreen
          total={total}
          paymentMethod={checkout?.payment_method || checkout?.selected_payment_method || 'card'}
          paymentStatus={paymentStatus}
        />
      )}

      {phase === PHASE.COMPLETE && (
        <CustomerPaymentCompleteScreen
          resultType={resultType}
          resultMessage={resultMessage}
          total={total}
          receiptData={receiptData}
          onContinue={handleCompleteContinue}
          onDone={resetToIdle}
          scheduleIdle={scheduleIdle}
        />
      )}

      {phase === PHASE.RECEIPT && (
        <CustomerReceiptOptionScreen onSelect={handleReceiptSelect} />
      )}

      {/* Connection badge */}
      {phase !== PHASE.IDLE && (
        <CustomerDisplayStatusBadge connected={connected} terminalId={terminalId} />
      )}

      {/* Reconnecting overlay */}
      {showReconnecting && (
        <div className="cfd-reconnecting-overlay" data-testid="reconnecting-overlay">
          <div className="cfd-reconnecting-box">
            <div className="cfd-ring-spinner small" />
            <span>Reconnecting...</span>
          </div>
        </div>
      )}
    </div>
  );
}
